<script lang="ts">
export default {
  name: "Skeleton",
  props: {
    isLoading: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: "text",
      validator: (value: string) => ["text", "card"].includes(value)
    },
    imageHeight: {
      type: String,
      default: "200px"
    },
    textLines: {
      type: Number,
      default: 3
    },
    columns: {
      type: Number,
      default: 1
    },
    textLineHeight: {
      type: Array as () => (string | number)[],
      default: () => []
    }
  }
};
</script>

<template>
  <div v-if="isLoading" class="skeleton" :class="`skeleton-${type}`">
    <template v-if="type === 'text'">
      <div class="skeleton-grid" :style="{ gridTemplateColumns: `repeat(${columns}, 1fr)` }">
        <div
          v-for="index in columns * textLines"
          :key="`text-${index}`"
          class="skeleton-bar"
          :style="{ height: textLineHeight[index - 1] || '20px' }"
        ></div>
      </div>
    </template>

    <!-- Card skeleton -->
    <template v-else-if="type === 'card'">
      <div class="skeleton-image" :style="{ height: imageHeight }"></div>
      <div class="skeleton-card-content">
        <div class="skeleton-card-title"></div>
        <div
          v-for="index in Math.min(textLines, 2)"
          :key="`card-text-${index}`"
          class="skeleton-card-text"
          :style="{ width: index === 1 ? '100%' : '70%' }"
        ></div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.skeleton {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.skeleton-grid {
  display: grid;
  gap: 12px;
}

.skeleton-bar {
  background: var(--mds_brand_appearance_neutral_weak_background-color);
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 20px;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      var(--mds_brand_appearance_neutral_strong_background-color),
      transparent
    );
    animation: skeleton-loading 1.2s infinite;
  }
}

.skeleton-image {
  width: 100%;
  background: var(--mds_brand_appearance_neutral_weak_background-color);
  border-radius: 8px 8px 0 0;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      var(--mds_brand_appearance_neutral_strong_background-color),
      transparent
    );
    animation: skeleton-loading 1.2s infinite;
  }
}

.skeleton-card-content {
  padding: 0 8px;
}

.skeleton-card-title {
  height: 24px;
  background: var(--mds_brand_appearance_neutral_weak_background-color);
  margin-bottom: 16px;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      var(--mds_brand_appearance_neutral_strong_background-color),
      transparent
    );
    animation: skeleton-loading 1.2s infinite;
  }
}

.skeleton-card-text {
  height: 16px;
  background: var(--mds_brand_appearance_neutral_weak_background-color);
  margin-bottom: 10px;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      var(--mds_brand_appearance_neutral_strong_background-color),
      transparent
    );
    animation: skeleton-loading 1.2s infinite;
  }
}

@keyframes skeleton-loading {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
